import {
  useRef,
  useEffect,
  useState,
  memo,
  useCallback,
  useMemo,
} from "react";
import {
  X,
  Paperclip,
  Image,
  Globe,
  Atom,
  Paintbrush,
  Check,
  Images,
  ChevronsLeftRight,
  ChevronsRightLeft,
  HelpCircle,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import EditIcon from "../../packages/svgs/EditIcon.tsx";
import CreateImageIcon from "../../packages/svgs/CreateImageIcon.tsx";
import { Textarea } from "@/components/ui/textarea";
import { SendControls } from "./SendControls";
import { AgentDropdown } from "./AgentDropdown";
import { AttachedMedia, AIAgent } from "@/types/aiAgents";
import { LocationButton } from "./LocationButton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsExtraSmall } from "@/hooks/use-extra-small";
import { aiAgents } from "@/services/aiAgentServices";
import { availableAgents } from "./AIAgentSelector";
import { AIAgentSelector } from "./AIAgentSelector";
import { isDocumentFile, getDocumentIcon } from "@/services/documentParseService";
import { convertFileToImages, getConvertibleFilePageCount } from "@/services/fileImageConverter";
import dragDropImage from "@/assets/oreog-drag-and-drop.png";
import { useToast } from "@/hooks/use-toast";
import DrawCanvasModal from "@/components/canvas/DrawCanvasModal";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";
import { VoiceModeOverlay } from "@/components/VoiceModeOverlay";
import { storage, AppSettings } from "@/utils/storageManager";
import { supabase } from "@/integrations/supabase/client";
import { consumeDailyQuota } from "@/services/dailyQuotaService";


interface ChatInputAreaProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  attachments: AttachedMedia[];
  onAttachmentsChange: (media: AttachedMedia[]) => void;
  // Legacy single-attachment props (optional for backward-compatibility)
  attachedMedia?: AttachedMedia | null;
  onMediaAttach?: (media: AttachedMedia | null) => void;
  isImageMode: boolean;
  isWebSearchMode: boolean;
  isDeepResearchMode: boolean;
  isQuizMode?: boolean;
  onModeChange: (mode: "chat" | "image" | "web" | "deep-research" | "quiz") => void;
  selectedAgent: AIAgent | null;
  onAgentChange: (agent: AIAgent | null) => void;
  onSend: () => void;
  onStop?: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  canSendMessage: boolean;
  isDarkMode: boolean;
  placeholderOverride?: string;
  fixedHeightPx?: number;
  agentDropdownPlacement?: 'above' | 'bottom';
  onOpenLibrary?: () => void;
  hasLibraryImages?: boolean;
  showMemeTemplates: boolean;
  selectedMemeTemplate: any;
  isVoiceModeOpen?: boolean;
  onVoiceModeChange?: (isOpen: boolean) => void;
  onExpandedChange?: (isExpanded: boolean) => void;
  isWelcomeScreen?: boolean;
}

export const ChatInputArea = memo(
  ({
    inputValue,
    onInputChange,
    attachments,
    onAttachmentsChange,
    attachedMedia: legacyAttachedMedia,
    onMediaAttach: legacyOnMediaAttach,
    isImageMode,
    isWebSearchMode,
    isDeepResearchMode,
    isQuizMode = false,
    onModeChange,
    selectedAgent,
    onAgentChange,
    onSend,
    onStop,
    onKeyPress,
    isLoading,
    canSendMessage,
    isDarkMode,
    placeholderOverride,
    fixedHeightPx,
    agentDropdownPlacement = 'above',
    showMemeTemplates,
    selectedMemeTemplate,
    onOpenLibrary,
    hasLibraryImages,
    isVoiceModeOpen: isVoiceModeOpenProp,
    onVoiceModeChange,
    onExpandedChange,
    isWelcomeScreen = false,
  }: ChatInputAreaProps) => {
    // Local shims to keep existing implementation working while migrating to multi-attachments
    const attachedMedia = (attachments && attachments.length > 0)
      ? attachments[0]
      : (legacyAttachedMedia ?? null);
    const onMediaAttach = (media: AttachedMedia | null) => {
      if (onAttachmentsChange) return onAttachmentsChange(media ? [media] : []);
      if (legacyOnMediaAttach) return legacyOnMediaAttach(media);
    };
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageUploadRef = useRef<HTMLInputElement>(null);
    const attachInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const photosInputRef = useRef<HTMLInputElement>(null);
    const [showAgentDropdown, setShowAgentDropdown] = useState(false);
    const [showAgentModal, setShowAgentModal] = useState(false);
    const [agentSearchQuery, setAgentSearchQuery] = useState("");
    const [showModeDropdown, setShowModeDropdown] = useState(false);
    const [modeSearchQuery, setModeSearchQuery] = useState("");
    const [selectedModeIndex, setSelectedModeIndex] = useState(0);
    const [shouldRenderModeDropdown, setShouldRenderModeDropdown] = useState(false);
    const [showPlusDropdown, setShowPlusDropdown] = useState(false);
    const [showMobileDrawer, setShowMobileDrawer] = useState(false);
    const [isListening, setIsListening] = useState(false);

    // Support both local and prop-based voice mode state
    const [isVoiceModeOpenLocal, setIsVoiceModeOpenLocal] = useState(false);
    const isVoiceModeOpen = isVoiceModeOpenProp ?? isVoiceModeOpenLocal;
    const setIsVoiceModeOpen = (val: boolean) => {
      if (onVoiceModeChange) {
        onVoiceModeChange(val);
      } else {
        setIsVoiceModeOpenLocal(val);
      }
    };

    const [isDragging, setIsDragging] = useState(false);
    const [ambientGradient, setAmbientGradient] = useState<string>("");
    const [isAspectDropdownOpen, setIsAspectDropdownOpen] = useState(false);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const aspectDropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const isExtraSmall = useIsExtraSmall();
    const { toast } = useToast();
    const attachmentsRef = useRef(attachments);
    useEffect(() => {
      attachmentsRef.current = attachments;
    }, [attachments]);

    const [backgroundType, setBackgroundType] = useState<"solid" | "image">(() => {
      try {
        const settings = storage.getAppSettings() as AppSettings;
        const bt = (settings?.backgroundType as "solid" | "image" | undefined) ?? "solid";
        return bt === "image" ? "image" : "solid";
      } catch {
        return "solid";
      }
    });

    useEffect(() => {
      const handleAppSettingsChange = (e: CustomEvent) => {
        const nextType = (e as any).detail?.backgroundType;
        if (nextType === "solid" || nextType === "image") {
          setBackgroundType(nextType);
        } else {
          setBackgroundType("solid");
        }
      };

      window.addEventListener(
        "appSettingsChanged",
        handleAppSettingsChange as EventListener
      );
      return () => {
        window.removeEventListener(
          "appSettingsChanged",
          handleAppSettingsChange as EventListener
        );
      };
    }, []);

    const isImageBackground = backgroundType === "image";
    const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
    const [showCanvas, setShowCanvas] = useState(false);
    const [canvasInitialImage, setCanvasInitialImage] = useState<string | null>(null);

    const aspectRatios = useMemo(() => {
      return [
        { id: "1:1", label: "1:1", w: 14, h: 14 },
        { id: "16:9", label: "16:9", w: 16, h: 9 },
        { id: "9:16", label: "9:16", w: 9, h: 16 },
        { id: "4:3", label: "4:3", w: 16, h: 12 },
        { id: "3:2", label: "3:2", w: 15, h: 10 },
        { id: "21:9", label: "21:9", w: 21, h: 9 },
      ];
    }, []);

    const [selectedAspectRatio, setSelectedAspectRatio] = useState<string>(() => {
      try {
        const v = localStorage.getItem('chatImageAspectRatio');
        return v || '1:1';
      } catch {
        return '1:1';
      }
    });

    useEffect(() => {
      try {
        localStorage.setItem('chatImageAspectRatio', selectedAspectRatio);
      } catch {
        // ignore
      }
    }, [selectedAspectRatio]);

    const AspectRatioIcon = useCallback(({ ratio }: { ratio: string }) => {
      const item = aspectRatios.find((r) => r.id === ratio) || aspectRatios[0];
      const max = Math.max(item.w, item.h);
      const rw = Math.max(6, Math.round((18 * item.w) / max));
      const rh = Math.max(6, Math.round((18 * item.h) / max));
      const x = Math.round((22 - rw) / 2);
      const y = Math.round((22 - rh) / 2);
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x={x}
            y={y}
            width={rw}
            height={rh}
            rx="3"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      );
    }, [aspectRatios]);


    const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
    const MAX_DOC_SIZE = 300 * 1024; // 300KB
    const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
    const ALLOWED_PDF_MIME_TYPES = new Set(["application/pdf"]);
    const MAX_ATTACHMENTS = 7;
    const MAX_DOC_PAGES_PER_MESSAGE = 8;

    const isAllowedImage = (file: File) => {
      const mimeType = file.type?.toLowerCase();
      if (mimeType && ALLOWED_IMAGE_MIME_TYPES.has(mimeType)) return true;

      const fileName = file.name?.toLowerCase() || "";
      return fileName.endsWith(".jpg") || fileName.endsWith(".jpeg") || fileName.endsWith(".png") || fileName.endsWith(".webp") || fileName.endsWith(".gif");
    };

    // helpers are defined after openCanvas to avoid use-before-declare

    const truncateDocName = useCallback((fileName: string, maxLen = 20) => {
      const name = (fileName || '').trim();
      if (!name) return 'Document';
      if (name.length <= maxLen) return name;

      const parts = name.split('.');
      const ext = parts.length > 1 ? parts.pop() : undefined;
      const base = parts.join('.') || name;

      if (!ext) return base.slice(0, maxLen);
      const suffix = `.${ext}`;
      const allowedBase = Math.max(1, maxLen - suffix.length);
      return `${base.slice(0, allowedBase)}${suffix}`;
    }, []);

    const isAllowedPdf = (file: File) => {
      const mimeType = file.type?.toLowerCase();
      if (mimeType && ALLOWED_PDF_MIME_TYPES.has(mimeType)) return true;

      const fileName = file.name?.toLowerCase() || "";
      return fileName.endsWith(".pdf");
    };

    // Accept other convertible docs by extension (handled by converter later)
    const isConvertibleDocByExt = (file: File) => {
      const name = (file.name || '').toLowerCase();
      return [
        '.pdf', '.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls', '.txt', '.md', '.json', '.xml', '.html'
      ].some(ext => name.endsWith(ext));
    };

    const getTotalDocPages = useCallback((list: AttachedMedia[]) => {
      return list
        .filter((m) => m?.type === 'document')
        .reduce((sum, m) => sum + (Number(m.pageCount) || 1), 0);
    }, []);

    const addAttachment = useCallback((media: AttachedMedia) => {
      if (attachments.length >= MAX_ATTACHMENTS) {
        toast({
          title: "Attachment limit reached",
          description: `You can attach up to ${MAX_ATTACHMENTS} files per message.`,
          variant: "destructive",
        });
        return false;
      }

      if (media.type === 'document') {
        const nextTotalPages = getTotalDocPages(attachments) + (Number(media.pageCount) || 1);
        if (nextTotalPages > MAX_DOC_PAGES_PER_MESSAGE) {
          toast({
            title: "Attachment limit reached",
            description: `You can attach up to ${MAX_DOC_PAGES_PER_MESSAGE} document pages per message.`,
            variant: "destructive",
          });
          return false;
        }
      }

      onAttachmentsChange([...attachments, media]);
      return true;
    }, [attachments, getTotalDocPages, onAttachmentsChange, toast]);

    const uploadImageInBg = useCallback(async (file: File, localUrl: string) => {
      try {
        const quota = await consumeDailyQuota("attachment", 1);
        if (!quota.allowed) {
          toast({
            title: "Attachment limit reached",
            description: quota.reason || "You've reached your daily attachment limit.",
            variant: "destructive",
          });
          onAttachmentsChange(attachmentsRef.current.filter(att => att.url !== localUrl));
          return;
        }
      } catch (err) {
        console.error("Quota check failed", err);
        toast({
          title: "Upload unavailable",
          description: "Could not verify your daily attachment quota.",
          variant: "destructive",
        });
        onAttachmentsChange(attachmentsRef.current.filter(att => att.url !== localUrl));
        return;
      }

      try {
        const fileToDataUrl = (f: File) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(f);
          });

        const imageBase64 = await fileToDataUrl(file);

        let uploadedUrl: string | null = null;
        try {
          const res = await supabase.functions.invoke("ChatAttachedImage", {
            body: {
              action: "ATTACHED_UPLOAD",
              imageBase64,
              filename: file.name,
              mime: file.type,
              quality: 90,
            },
          });

          const result: any = (res as any).data || res;
          uploadedUrl = result?.imageUrl || null;
        } catch (err) {
          console.error("Attachment upload failed", err);
        }

        if (uploadedUrl) {
          onAttachmentsChange(
            attachmentsRef.current.map((att) =>
              att.url === localUrl
                ? {
                    ...att,
                    url: uploadedUrl,
                    file: undefined,
                    uploading: false,
                  }
                : att
            )
          );
        } else {
          toast({
            title: "Upload failed",
            description: `Could not upload image "${file.name}".`,
            variant: "destructive",
          });
          onAttachmentsChange(attachmentsRef.current.filter(att => att.url !== localUrl));
        }
      } catch (err) {
        console.error("Background upload error", err);
        onAttachmentsChange(attachmentsRef.current.filter(att => att.url !== localUrl));
      }
    }, [onAttachmentsChange, toast]);

    const processSelectedFiles = useCallback(async (files: File[]) => {
      const remaining = Math.max(0, MAX_ATTACHMENTS - attachmentsRef.current.length);
      if (remaining === 0) {
        toast({ title: "Attachment limit reached", description: `You can attach up to ${MAX_ATTACHMENTS} files per message.`, variant: "destructive" });
        return;
      }

      const filesToProcess = files.slice(0, remaining);
      const imagesToUpload: { file: File; localUrl: string; name: string }[] = [];
      const docsToProcess: File[] = [];

      for (const file of filesToProcess) {
        if (!file || typeof file.size !== 'number' || file.size <= 0) {
          toast({ title: "Invalid file", description: "This file can't be uploaded. Please try again.", variant: "destructive" });
          continue;
        }
        if (isAllowedImage(file)) {
          if (file.size > MAX_IMAGE_SIZE) {
            toast({ title: "Image too large", description: "Please upload images up to 5 MB.", variant: "destructive" });
            continue;
          }
          const localUrl = URL.createObjectURL(file);
          imagesToUpload.push({ file, localUrl, name: file.name });
        } else if (isDocumentFile(file) || isConvertibleDocByExt(file)) {
          docsToProcess.push(file);
        } else {
          toast({ title: "Unsupported file type", description: "Only images and supported documents (PDF, DOCX, PPTX, TXT, etc.) can be attached.", variant: "destructive" });
        }
      }

      if (imagesToUpload.length === 0 && docsToProcess.length === 0) {
        return;
      }

      const newAttachments: AttachedMedia[] = [];

      for (const img of imagesToUpload) {
        newAttachments.push({
          type: "image",
          url: img.localUrl,
          name: img.name,
          file: img.file,
          uploading: true,
        });
      }

      setIsAttachmentUploading(true);
      try {
        let totalPages = getTotalDocPages(attachmentsRef.current);
        for (const docFile of docsToProcess) {
          const safeName = truncateDocName(docFile.name, 20);
          try {
            const pageCount = await getConvertibleFilePageCount(docFile);
            const safeCount = Math.max(1, Math.min(MAX_DOC_PAGES_PER_MESSAGE, Number(pageCount) || 1));
            if (totalPages + safeCount > MAX_DOC_PAGES_PER_MESSAGE) {
              toast({
                title: 'Attachment limit reached',
                description: `You can attach up to ${MAX_DOC_PAGES_PER_MESSAGE} document pages per message.`,
                variant: 'destructive',
              });
              continue;
            }
            totalPages += safeCount;
            const converted = (await convertFileToImages(docFile)).slice(0, safeCount);
            newAttachments.push({
              type: "document",
              url: URL.createObjectURL(docFile),
              name: safeName,
              file: docFile,
              pageCount: safeCount,
              convertedImageUrls: converted.map((ci) => ci.dataUrl),
            });
          } catch {
            if (totalPages + 1 > MAX_DOC_PAGES_PER_MESSAGE) {
              toast({
                title: 'Attachment limit reached',
                description: `You can attach up to ${MAX_DOC_PAGES_PER_MESSAGE} document pages per message.`,
                variant: 'destructive',
              });
              continue;
            }
            totalPages += 1;
            try {
              const converted = (await convertFileToImages(docFile)).slice(0, 1);
              newAttachments.push({
                type: "document",
                url: URL.createObjectURL(docFile),
                name: safeName,
                file: docFile,
                pageCount: 1,
                convertedImageUrls: converted.map((ci) => ci.dataUrl),
              });
            } catch {
              newAttachments.push({
                type: "document",
                url: URL.createObjectURL(docFile),
                name: safeName,
                file: docFile,
                pageCount: 1,
              });
            }
          }
        }
      } finally {
        setIsAttachmentUploading(false);
      }

      if (newAttachments.length > 0) {
        onAttachmentsChange([...attachmentsRef.current, ...newAttachments]);
        setShowPlusDropdown(false);

        for (const img of imagesToUpload) {
          uploadImageInBg(img.file, img.localUrl);
        }
      }
    }, [onAttachmentsChange, uploadImageInBg, getTotalDocPages, truncateDocName, toast]);

    const [isExpanded, setIsExpanded] = useState(false);
    const [showExpandButton, setShowExpandButton] = useState(false);

    // Notify parent when expanded state changes
    useEffect(() => {
      onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    useEffect(() => {
      if (!textareaRef.current) return;

      // For welcome screen: don't use fixedHeightPx when expanded or when we want auto-resize
      const shouldUseFixedHeight = typeof fixedHeightPx === 'number' && fixedHeightPx > 0 && !isExpanded && isWelcomeScreen;

      if (shouldUseFixedHeight) {
        // For welcome screen, still allow some auto-resize within bounds
        textareaRef.current.style.transition = 'height 0.2s ease-out';
        const scrollHeight = textareaRef.current.scrollHeight;
        const targetHeight = Math.min(Math.max(scrollHeight, fixedHeightPx), 300);
        textareaRef.current.style.height = `${targetHeight}px`;

        // Show expand button when text is substantial (more than 80 characters or scrollHeight > 150)
        if (inputValue.length > 80 || scrollHeight > 150) {
          setShowExpandButton(true);
        } else {
          setShowExpandButton(false);
        }
        return;
      }

      if (typeof fixedHeightPx === 'number' && fixedHeightPx > 0 && !isWelcomeScreen) {
        textareaRef.current.style.height = `${fixedHeightPx}px`;
        return;
      }

      // Reset height to auto to get correct scrollHeight
      textareaRef.current.style.transition = 'none';
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;

      // Check if we should show expand button (approx 224px is max-h-56)
      if (scrollHeight > 224 || (isWelcomeScreen && inputValue.length > 80)) {
        setShowExpandButton(true);
      } else {
        setShowExpandButton(false);
        // Auto-collapse if text becomes short
        if (isExpanded && scrollHeight <= 224 && inputValue.length <= 80) {
          setIsExpanded(false);
        }
      }

      if (isExpanded) {
        textareaRef.current.style.transition = 'height 0.3s ease-in-out';
        textareaRef.current.style.height = isWelcomeScreen ? "60vh" : "80vh";
      } else {
        // No transition during normal auto-resize
        textareaRef.current.style.height = `${scrollHeight}px`;
      }
    }, [fixedHeightPx, inputValue, isExpanded, isWelcomeScreen]);

    // Reset expansion on send if empty
    useEffect(() => {
      if (!inputValue && isExpanded) {
        setIsExpanded(false);
      }
    }, [inputValue, isExpanded]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (!aspectDropdownRef.current) return;
        if (aspectDropdownRef.current.contains(event.target as Node)) return;
        setIsAspectDropdownOpen(false);
      };

      if (isAspectDropdownOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isAspectDropdownOpen]);

    // Focus textarea when global event is dispatched (from selection tooltip)
    useEffect(() => {
      const handleFocus = () => {
        if (textareaRef.current) {
          textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          textareaRef.current.focus();
        }
      };
      window.addEventListener('focusChatInput', handleFocus as EventListener);
      return () => {
        window.removeEventListener('focusChatInput', handleFocus as EventListener);
      };
    }, []);

    const [canvasTargetIndex, setCanvasTargetIndex] = useState<number | null>(null);

    const openCanvas = useCallback((initial?: string | null, targetIndex: number | null = null) => {
      setCanvasInitialImage(initial || null);
      setCanvasTargetIndex(targetIndex);
      setShowCanvas(true);
      setShowPlusDropdown(false);
      setShowAgentDropdown(false);
    }, []);

    const handleCanvasDone = useCallback((dataUrl: string, file: File) => {
      const url = URL.createObjectURL(file);
      const newMedia: AttachedMedia = { type: "image", url, name: file.name, file, uploading: true };

      if (canvasTargetIndex !== null && canvasTargetIndex >= 0 && canvasTargetIndex < attachments.length) {
        const next = attachments.slice();
        next[canvasTargetIndex] = newMedia;
        onAttachmentsChange(next);
      } else {
        // append if space available
        if (attachments.length >= MAX_ATTACHMENTS) {
          toast({
            title: "Attachment limit reached",
            description: `You can attach up to ${MAX_ATTACHMENTS} files per message.`,
            variant: "destructive",
          });
          setShowCanvas(false);
          setCanvasInitialImage(null);
          setCanvasTargetIndex(null);
          return;
        } else {
          onAttachmentsChange([...attachments, newMedia]);
        }
      }
      setShowCanvas(false);
      setCanvasInitialImage(null);
      setCanvasTargetIndex(null);
      uploadImageInBg(file, url);
    }, [attachments, canvasTargetIndex, onAttachmentsChange, toast, uploadImageInBg]);



    // Handle drag and drop events for the entire window
    useEffect(() => {
      // Helper to determine if the drag event actually contains files
      const isFileDrag = (e: DragEvent) => {
        const dt = e.dataTransfer;
        if (!dt) return false;
        // Prefer checking types for 'Files'
        if (dt.types && Array.from(dt.types).includes('Files')) return true;
        // Fallback: inspect items
        if (dt.items && dt.items.length > 0) {
          return Array.from(dt.items).some((item) => item.kind === 'file');
        }
        return false;
      };

      const handleDragEnter = (e: DragEvent) => {
        if (!isFileDrag(e)) return; // ignore text/URL drags
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
      };

      const handleDragOver = (e: DragEvent) => {
        if (!isFileDrag(e)) return; // ignore text/URL drags
        e.preventDefault();
        e.stopPropagation();
        // indicate this is a copy operation
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        if (!isDragging) setIsDragging(true);
      };

      const handleDragLeave = (e: DragEvent) => {
        if (!isFileDrag(e)) return; // ignore non-file drags
        e.preventDefault();
        e.stopPropagation();
        // Only hide if we're leaving the window
        if (isDragging && (e as any).relatedTarget === null) {
          setIsDragging(false);
        }
      };

      const handleDrop = (e: DragEvent) => {
        if (!isFileDrag(e)) return; // allow normal text drops
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          processSelectedFiles(Array.from(e.dataTransfer.files));
        }
      };

      const handleDragEnd = () => {
        if (isDragging) {
          setIsDragging(false);
        }
      };

      const handlePointerUp = () => {
        if (isDragging) {
          setIsDragging(false);
        }
      };

      // Add event listeners to the window for full-screen drag and drop
      window.addEventListener("dragenter", handleDragEnter);
      window.addEventListener("dragover", handleDragOver);
      window.addEventListener("dragleave", handleDragLeave);
      window.addEventListener("drop", handleDrop);
      window.addEventListener("dragend", handleDragEnd);
      window.addEventListener("pointerup", handlePointerUp, { passive: true });
      window.addEventListener("touchend", handlePointerUp, { passive: true });
      window.addEventListener("touchcancel", handlePointerUp, { passive: true });

      return () => {
        window.removeEventListener("dragenter", handleDragEnter);
        window.removeEventListener("dragover", handleDragOver);
        window.removeEventListener("dragleave", handleDragLeave);
        window.removeEventListener("drop", handleDrop);
        window.removeEventListener("dragend", handleDragEnd);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("touchend", handlePointerUp);
        window.removeEventListener("touchcancel", handlePointerUp);
      };
    }, [isDragging, addAttachment, toast]);

    useEffect(() => {
      const onStart = () => setIsAttachmentUploading(true);
      const onDone = () => setIsAttachmentUploading(false);
      window.addEventListener('attachmentUpload:start', onStart as EventListener);
      window.addEventListener('attachmentUpload:done', onDone as EventListener);
      return () => {
        window.removeEventListener('attachmentUpload:start', onStart as EventListener);
        window.removeEventListener('attachmentUpload:done', onDone as EventListener);
      };
    }, []);

    // When mobile drawer is open, prevent background (chat) from scrolling
    useEffect(() => {
      if (typeof document === 'undefined') return;
      if (!isMobile) return;

      const body = document.body;
      const originalOverflow = body.style.overflow;

      if (showMobileDrawer) {
        body.style.overflow = 'hidden';
        return () => {
          body.style.overflow = originalOverflow;
        };
      }

      // Ensure overflow is reset when drawer is closed
      body.style.overflow = originalOverflow;
    }, [showMobileDrawer, isMobile]);

    const handleInputChange = useCallback(
      (value: string) => {
        // Always update the input value first
        onInputChange(value);

        if (value === "/") {
          setShowAgentDropdown(true);
          setAgentSearchQuery("");
          setShowModeDropdown(false);
          setModeSearchQuery("");
        } else if (value.startsWith("/")) {
          const searchQuery = value.slice(1); // Remove the slash
          // Check if the query contains spaces - if so, close dropdown
          if (searchQuery.includes(" ")) {
            setShowAgentDropdown(false);
            setAgentSearchQuery("");
          } else {
            setShowAgentDropdown(true);
            setAgentSearchQuery(searchQuery);
          }
          setShowModeDropdown(false);
          setModeSearchQuery("");
        } else if (value === "@") {
          setShowModeDropdown(true);
          setModeSearchQuery("");
          setSelectedModeIndex(0);
          setShowAgentDropdown(false);
          setAgentSearchQuery("");
        } else if (value.startsWith("@")) {
          const searchQuery = value.slice(1);
          if (searchQuery.includes(" ")) {
            setShowModeDropdown(false);
            setModeSearchQuery("");
          } else {
            setShowModeDropdown(true);
            setModeSearchQuery(searchQuery);
            setSelectedModeIndex(0);
          }
          setShowAgentDropdown(false);
          setAgentSearchQuery("");
        } else {
          if (showAgentDropdown) {
            setShowAgentDropdown(false);
            setAgentSearchQuery("");
          }
          if (showModeDropdown) {
            setShowModeDropdown(false);
            setModeSearchQuery("");
          }
        }
      },
      [onInputChange, showAgentDropdown, showModeDropdown]
    );

    const modeItems = useMemo(() => {
      return [
        {
          id: 'image' as const,
          label: 'Create image',
          description: 'Visualize anything',
          icon: Image,
        },
        {
          id: 'quiz' as const,
          label: 'Quiz',
          description: 'Get an interactive quiz',
          icon: HelpCircle,
        },
        {
          id: 'web' as const,
          label: 'Web search',
          description: 'Find real-time news and info',
          icon: Globe,
        },
        {
          id: 'deep-research' as const,
          label: 'Deep research',
          description: 'Get a detailed report',
          icon: Atom,
        },
      ];
    }, []);

    const filteredModeItems = useMemo(() => {
      const q = (modeSearchQuery || '').trim().toLowerCase();
      if (!q) return modeItems;
      return modeItems.filter((m) =>
        m.label.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q) ||
        m.id.toLowerCase().includes(q)
      );
    }, [modeItems, modeSearchQuery]);

    useEffect(() => {
      setSelectedModeIndex(0);
    }, [modeSearchQuery]);

    useEffect(() => {
      if (showModeDropdown) {
        setShouldRenderModeDropdown(true);
        return;
      }

      const t = window.setTimeout(() => setShouldRenderModeDropdown(false), 200);
      return () => window.clearTimeout(t);
    }, [showModeDropdown]);

    const handleAgentSelect = useCallback(
      (agent: AIAgent) => {
        onAgentChange(agent);
        onInputChange("");
        setIsExpanded(false);
        setShowAgentDropdown(false);
        setAgentSearchQuery("");
        textareaRef.current?.focus();
      },
      [onAgentChange, onInputChange]
    );

    const handleModeSelect = useCallback(
      (mode: "chat" | "image" | "web" | "deep-research" | "quiz") => {
        onModeChange(mode);
        onAgentChange(null);
        onInputChange("");
        setShowModeDropdown(false);
        setModeSearchQuery("");
        textareaRef.current?.focus();
      },
      [onModeChange, onAgentChange, onInputChange]
    );

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (showAgentDropdown) {
        return;
      }
      if (showModeDropdown) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedModeIndex((prev) => Math.min(prev + 1, Math.max(0, filteredModeItems.length - 1)));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedModeIndex((prev) => Math.max(prev - 1, 0));
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const onlyOne = filteredModeItems.length === 1 ? filteredModeItems[0] : null;
          const selected = onlyOne || filteredModeItems[selectedModeIndex];
          if (selected) {
            handleModeSelect(selected.id);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowModeDropdown(false);
          setModeSearchQuery('');
          return;
        }
        return;
      }
      onKeyPress(e);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const filesList = event.target.files ? Array.from(event.target.files) : [];
      if (filesList.length === 0) return;
      processSelectedFiles(filesList);

      if (fileInputRef.current) fileInputRef.current.value = "";
      if (attachInputRef.current) attachInputRef.current.value = "";
      if (photosInputRef.current) photosInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      if (files.length > 0) {
        processSelectedFiles(files);
      }

      if (imageUploadRef.current) {
        imageUploadRef.current.value = "";
      }
      if (cameraInputRef.current) {
        cameraInputRef.current.value = "";
      }
    };

    const openMobileDrawer = useCallback(() => {
      setShowPlusDropdown(false);
      setShowAgentDropdown(false);
      setShowMobileDrawer(true);
    }, []);

    const closeMobileDrawer = useCallback(() => {
      setShowMobileDrawer(false);
    }, []);

    const handleAgentModalChange = useCallback((agent: AIAgent | null) => {
      onAgentChange(agent);
      onModeChange('chat');
      setShowAgentModal(false);
      textareaRef.current?.focus();
    }, [onAgentChange, onModeChange]);

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const dt = e.clipboardData;
      if (!dt) return;

      const files: File[] = [];
      if (dt.files && dt.files.length > 0) {
        for (let i = 0; i < dt.files.length; i++) files.push(dt.files[i]);
      } else if (dt.items && dt.items.length > 0) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const f = item.getAsFile();
            if (f) files.push(f);
          }
        }
      }

      if (files.length === 0) return;
      e.preventDefault();
      processSelectedFiles(files);
    }, [processSelectedFiles]);

    const getCurrentModeText = useMemo(() => {
      if (selectedAgent) return selectedAgent.name;
      if (isImageMode) return isMobile ? "Image" : "Image";
      if (isQuizMode) return isMobile ? "Quiz" : "Quiz";
      if (isWebSearchMode) return isMobile ? "Web" : "Web Search";
      if (isDeepResearchMode) return isMobile ? "Research" : "Deep Research";
      return isMobile ? "" : "Chat";
    }, [selectedAgent, isImageMode, isQuizMode, isWebSearchMode, isDeepResearchMode, isMobile]);

    const currentMode = useMemo((): 'chat' | 'image' | 'web' | 'deep-research' | 'quiz' => {
      if (isImageMode) return 'image';
      if (isQuizMode) return 'quiz';
      if (isWebSearchMode) return 'web';
      if (isDeepResearchMode) return 'deep-research';
      return 'chat';
    }, [isImageMode, isQuizMode, isWebSearchMode, isDeepResearchMode]);

    const showAgentsQuickButton = useMemo(() => {
      if (!isMobile) return true;
      if (isListening) return false;
      // On mobile, hide Agents quick button when any non-chat mode is active
      return currentMode === 'chat';
    }, [currentMode, isListening, isMobile]);

    // Extract dominant colors from image for ambient mode (adaptive + subtle)
    const extractImageColors = useCallback((imageUrl: string) => {
      const img = document.createElement('img');
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Small canvas for better performance
        const size = 50;
        canvas.width = size;
        canvas.height = size;
        ctx.drawImage(img, 0, 0, size, size);

        try {
          const imageData = ctx.getImageData(0, 0, size, size);
          const data = imageData.data;
          const colors: { r: number; g: number; b: number; count: number }[] = [];

          // Sample and cluster similar colors
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const alpha = data[i + 3];

            if (alpha < 128) continue;

            const threshold = 30;
            let found = false;

            for (const color of colors) {
              if (
                Math.abs(color.r - r) < threshold &&
                Math.abs(color.g - g) < threshold &&
                Math.abs(color.b - b) < threshold
              ) {
                color.count++;
                found = true;
                break;
              }
            }

            if (!found) colors.push({ r, g, b, count: 1 });
          }

          // Sort by frequency
          colors.sort((a, b) => b.count - a.count);

          // Helper to compute color distance
          const dist = (c1: any, c2: any) => {
            const dr = c1.r - c2.r;
            const dg = c1.g - c2.g;
            const db = c1.b - c2.b;
            return Math.sqrt(dr * dr + dg * dg + db * db);
          };

          if (colors.length >= 1) {
            const primary = colors[0];
            // Pick a secondary sufficiently different from primary
            let secondary = colors.find((c) => dist(c, primary) >= 60) || colors[1] || colors[0];

            // Base opacity (stronger than before)
            let a1 = isDarkMode ? 0.35 : 0.22;
            let a2 = isDarkMode ? 0.25 : 0.16;
            let a3 = isDarkMode ? 0.15 : 0.10;

            // Luminance-based adjustment (boost opacity for very dark or very light palettes)
            const lum = (c: any) => (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
            const lp = lum(primary);
            const ls = lum(secondary || primary);
            const avgLum = (lp + ls) / 2;
            if (avgLum < 0.25 || avgLum > 0.85) {
              a1 = Math.min(a1 + 0.08, 0.5);
              a2 = Math.min(a2 + 0.06, 0.45);
              a3 = Math.min(a3 + 0.05, 0.35);
            }

            let gradient: string;
            if (secondary) {
              gradient = `linear-gradient(135deg,
                rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a1}) 0%,
                rgba(${secondary.r}, ${secondary.g}, ${secondary.b}, ${a2}) 50%,
                rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a3}) 100%)`;
            } else {
              gradient = `linear-gradient(135deg,
                rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a1}) 0%,
                rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a3}) 100%)`;
            }
            setAmbientGradient(gradient);
          }
        } catch (error) {
          console.warn('Could not extract colors:', error);
          const fallback = isDarkMode
            ? `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.10) 100%)`
            : `linear-gradient(135deg, rgba(0,0,0,0.14) 0%, rgba(0,0,0,0.08) 100%)`;
          setAmbientGradient(fallback);
        }
      };

      img.onerror = () => {
        const fallback = isDarkMode
          ? `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.10) 100%)`
          : `linear-gradient(135deg, rgba(0,0,0,0.14) 0%, rgba(0,0,0,0.08) 100%)`;
        setAmbientGradient(fallback);
      };
      img.src = imageUrl;
    }, [isDarkMode]);

    // Extract colors using first image in attachments
    useEffect(() => {
      const firstImage = attachments.find(m => m.type === 'image' && !!m.url);
      if (firstImage?.url) {
        extractImageColors(firstImage.url);
      } else {
        setAmbientGradient('');
      }
      // Recompute ambient when theme changes for better contrast
    }, [attachments, extractImageColors, isDarkMode]);

    return (
      <div
        className={`relative transition-colors duration-150 border rounded-md bg-transparent pt-0 ${isDarkMode ? " border-none" : " border-none"
          }`}
      >
        <div className="relative max-w-4xl mx-auto">
          <div className="flex flex-col">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.json,.xml,.html"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            <input
              ref={photosInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            <input
              ref={imageUploadRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif"
              onChange={handleImageUpload}
              className="hidden"
            />

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageUpload}
              className="hidden"
            />

            <input
              ref={attachInputRef}
              type="file"
              accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.json,.xml,.html"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Weather Location Button */}
            {selectedAgent?.id === "weather" && (
              <div className="flex justify-center">
                <LocationButton
                  onLocationFetched={(location) => {
                    onInputChange(location);
                    setTimeout(() => onSend(), 100);
                  }}
                  isDarkMode={isDarkMode}
                />
              </div>
            )}

            {/* Currency Converter */}

            {/* Media Preview */}
            {attachments.length > 0 && (
              <div className={`mb-0 overflow-x-auto thin-scrollbar ${isDarkMode ? 'bg-[#282828] border-[#373737]' : 'bg-gray-100 border-gray-200'} rounded-t-[25px] p-[10px] border border-b-0`}>
                <div className="flex flex-nowrap gap-2 w-max">
                  {attachments.slice(0, MAX_ATTACHMENTS).map((media, index) => (
                    <div key={media.url + index} className="relative">
                      {media.type === 'image' ? (
                        <div
                          className="w-[120px] h-[120px] rounded-2xl p-0 relative group"
                          style={{
                            backgroundImage: ambientGradient || undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transition: 'background-image 500ms ease',
                            ...(ambientGradient ? { backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' } : {}),
                          }}
                          onClick={() => {
                            if (media.type !== 'document') {
                              const modal = document.createElement('div');
                              modal.className = 'fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4';
                              modal.onclick = () => modal.remove();
                              const img = document.createElement('img');
                              img.src = media.url;
                              img.className = 'max-w-full max-h-full object-contain rounded-lg';
                              modal.appendChild(img);
                              document.body.appendChild(modal);
                            }
                          }}
                        >
                          <img
                            src={media.url}
                            alt={media.name || 'Image'}
                            className="w-full h-full object-cover rounded-xl cursor-pointer"
                          />

                          <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); openCanvas(media.url, index); }}
                            className={`absolute right-7 top-0.5 flex justify-center items-center rounded-full p-1 transition-all duration-200 backdrop-blur-sm ${isDarkMode ? 'bg-black/30 hover:bg-transparent text-white' : 'bg-white/80 hover:bg-transparent text-gray-900'}`}
                            aria-label="Annotate"
                          >
                            <EditIcon size="16" />
                          </button>

                          <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); onAttachmentsChange(attachments.filter((_, i) => i !== index)); }}
                            className={`absolute right-0.5 top-0.5 flex justify-center items-center rounded-full p-1 transition-all duration-200 backdrop-blur-sm ${isDarkMode ? 'bg-black/30 hover:bg-transparent text-white' : 'bg-white/80 hover:bg-transparent text-gray-900'}`}
                            aria-label="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>

                          {(media.uploading || isAttachmentUploading) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-2xl">
                              <div className="h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="group/thumbnail" data-testid="file-thumbnail">
                          <button
                            type="button"
                            className={`rounded-lg text-left block cursor-pointer transition-all rounded-lg border flex flex-col justify-between gap-2.5 overflow-hidden px-2.5 py-2 shadow-sm w-[120px] h-[120px] min-w-[120px] ${isDarkMode ? 'bg-[#1f1f1f] border-[#3a3a3a]' : 'bg-white border-gray-200'} ${isDarkMode ? 'hover:border-[#5a5a5a]' : 'hover:border-gray-300'} hover:shadow-md`}
                            onClick={() => {
                              // Intentionally no-op: file thumbnail preview only
                            }}
                          >
                            <div className="flex flex-col gap-1 min-h-0">
                              <h3 className={`text-[12px] break-words line-clamp-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                {media.name || (media.type === 'video' ? 'Video' : 'Document')}
                              </h3>
                              <p className={`text-[10px] line-clamp-1 break-words ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {media.type === 'document'
                                  ? `${Number(media.pageCount) || 1} pages`
                                  : media.file?.size
                                    ? `${(media.file.size / 1024 / 1024).toFixed(2)} MB`
                                    : media.type.toUpperCase()}
                              </p>
                            </div>
                            <div>
                              <div className="relative flex flex-row items-center gap-1 justify-between">
                                <div className="flex flex-row gap-1 shrink min-w-0">
                                  <div className={`min-w-0 h-[18px] flex flex-row items-center justify-center gap-0.5 px-1 border shadow-sm rounded font-medium ${isDarkMode ? 'border-[#3a3a3a] bg-[#2a2a2a]/70 text-gray-200' : 'border-gray-200 bg-white/70 text-gray-700'}`}>
                                    <p className="uppercase truncate text-[11px] leading-[13px]">
                                      {(() => {
                                        const name = (media.name || '').toLowerCase();
                                        const ext = name.includes('.') ? name.split('.').pop() : '';
                                        return ext || (media.type === 'video' ? 'video' : 'file');
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => onAttachmentsChange(attachments.filter((_, i) => i !== index))}
                            className={`transition-all w-5 h-5 absolute right-0.5 top-0.5 rounded-full bg-transparent flex items-center justify-center opacity-100 md:opacity-0 md:group-hover/thumbnail:opacity-100 backdrop-blur-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                            aria-label="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Text Input Area with controls */}
            <div
              className={`relative ${isDarkMode ? `sm:bg-[#1e1e1e]` : `sm:bg-white`
                }max-w-auto scrollbar-none sm:rounded-xl sm:rounded-br-none sm:rounded-bl-none`}
            >
              <div className="relative z-[1]">
                {isDragging && (
                  <div className="fixed inset-0 bg-white/80 dark:bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 pointer-events-none">
                    {/* Center content */}
                    <div className="text-center h-[max-content] bg-transparent p-4 max-w-md mx-auto flex flex-col items-center w-full">
                      <img
                        src={dragDropImage}
                        alt="Drag and Drop"
                        className="w-[300px] h-[300px] mb-6 object-contain select-none drag-none opacity-80"
                      />
                      <h1 className="text-gray-600 select-none text-4xl font-bold dark:text-gray-300">
                        Drop Your Files Here
                      </h1>
                      <p className="text-gray-500 select-none text-sm mt-2 dark:text-gray-400">
                        Images and supported documents (PDF, DOCX, PPTX, TXT, etc.)
                      </p>
                    </div>
                  </div>
                )}
                <Textarea
                  id="text"
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onPaste={handlePaste}
                  style={
                    typeof fixedHeightPx === 'number' && fixedHeightPx > 0 && !isWelcomeScreen
                      ? { height: fixedHeightPx, minHeight: fixedHeightPx, maxHeight: fixedHeightPx }
                      : undefined
                  }
                  placeholder={
                    placeholderOverride ??
                    (showMemeTemplates
                      ? "Enter template number or describe the meme"
                      : selectedMemeTemplate
                        ? "Enter meme text: 'Top text | Bottom text'"
                        : selectedAgent?.id === "weather"
                          ? "Enter city name or use location button above"
                          : isImageMode
                            ? "Describe the image you want to create..."
                            : isWebSearchMode
                              ? "Search the web..."
                              : "Type your message... (use / for AI agents)")
                  }
                  className={`outline-none focus:outline-none focus:ring-0 ring-0 min-h-[10px] sm:min-h-[10px] resize-none pr-3 sm:pr-4 pl-4 pb-14 sm:pb-16 pt-[13px] text-sm sm:text-base transition-none will-change-auto sm:border rounded-[25px] ${typeof fixedHeightPx === 'number' && fixedHeightPx > 0 && !isWelcomeScreen ? 'overflow-y-auto' : ''} ${isExpanded ? (isWelcomeScreen ? 'max-h-[60vh] overflow-y-auto' : 'max-h-[80vh] overflow-y-auto') : 'max-h-56'} ${isImageBackground
                    ? isDarkMode
                      ? "bg-[rgba(37,37,37,0.8)] backdrop-blur-[22px] backdrop-saturate-200 border-white/15 shadow-none text-white placeholder:text-white/40"
                      : "bg-white/80 backdrop-blur-[22px] backdrop-saturate-200 border-black/10 shadow-none text-gray-900 placeholder:text-gray-400"
                    : isDarkMode
                      ? "bg-[#252525] border-[#373737] focus:border-[#424242] text-white placeholder:text-gray-400"
                      : "bg-gray-100 border-gray-200 text-gray-900 focus:border-gray-300 placeholder:text-black/60"
                    } ${attachments.length > 0 ? `border-t-0 sm:border-t-0 rounded-t-none rounded-b-[25px] ${!isDarkMode ? 'focus:border-gray-200' : 'focus:border-[#373737] '}` : 'rounded-[25px]'}`}
                  rows={1}
                />

                {showExpandButton && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`absolute right-2 top-2 p-1.5 rounded-full backdrop-blur-sm transition-all z-20 ${isDarkMode
                      ? 'bg-black/20 hover:bg-black/40 text-gray-400 hover:text-white'
                      : 'bg-white/20 hover:bg-white/40 text-gray-500 hover:text-gray-900'
                      }`}
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? (
                      <ChevronsRightLeft className="w-4 h-4" style={{ transform: 'rotate(45deg)' }} />
                    ) : (
                      <ChevronsLeftRight className="w-4 h-4" style={{ transform: 'rotate(45deg)' }} />
                    )}
                  </button>
                )}

                {/* Agent Dropdown */}
                {agentDropdownPlacement === 'bottom' ? (
                  showAgentDropdown && (
                    <div className="fixed z-[1000] px-4 pb-4 pt-2 pointer-events-none">
                      <div
                        className={`w-full max-w-lg mx-auto rounded-3xl border shadow-2xl overflow-hidden pointer-events-auto ${isDarkMode
                          ? 'bg-[#1e1e1e] border-white/10'
                          : 'bg-white border-gray-200'
                          }`}
                      >
                        <AgentDropdown
                          agents={aiAgents}
                          onAgentSelect={handleAgentSelect}
                          isDarkMode={isDarkMode}
                          inputRef={textareaRef}
                          onClose={() => {
                            setShowAgentDropdown(false);
                            setAgentSearchQuery("");
                          }}
                          searchQuery={agentSearchQuery}
                          open={showAgentDropdown}
                          placement="bottom"
                        />
                      </div>
                    </div>
                  )
                ) : (
                  <AgentDropdown
                    agents={aiAgents}
                    onAgentSelect={handleAgentSelect}
                    isDarkMode={isDarkMode}
                    inputRef={textareaRef}
                    onClose={() => {
                      setShowAgentDropdown(false);
                      setAgentSearchQuery("");
                    }}
                    searchQuery={agentSearchQuery}
                    open={showAgentDropdown}
                    placement="above"
                  />
                )}

                {/* Mode Dropdown (@) */}
                {shouldRenderModeDropdown && (
                  <div
                    className={`absolute bottom-full left-0 mb-2 w-[260px] rounded-2xl border shadow-lg z-50 transform-gpu transition-all duration-200 ${showModeDropdown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
                      } ${isDarkMode
                        ? 'bg-[#1e1e1e] backdrop-blur-xl border-[#373737]'
                        : 'bg-white/95 backdrop-blur-xl border-gray-300'
                      }`}
                  >
                    <div className="p-2">
                      <div className={`text-xs font-medium mb-2 px-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {modeSearchQuery ? `Searching for "${modeSearchQuery}"` : 'Select a Mode'}
                      </div>
                      <div className="flex flex-col gap-1">
                        {filteredModeItems.length === 0 ? (
                          <div className={`text-center py-4 px-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            No modes found matching "{modeSearchQuery}"
                          </div>
                        ) : (
                          filteredModeItems.map((item, idx) => {
                            const Icon = item.icon;
                            const active = selectedModeIndex === idx;
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleModeSelect(item.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all duration-200 ${active
                                  ? isDarkMode
                                    ? 'bg-[#303030] text-white'
                                    : 'bg-gray-200 text-gray-900'
                                  : isDarkMode
                                    ? 'text-gray-300 hover:bg-[#2c2c2c] hover:text-white'
                                    : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                                  }`}
                              >
                                <Icon className="w-5 h-5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{item.label}</div>
                                  <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bottom Controls */}
                <div className="absolute bottom-2 left-2 right-2 flex xs:pl-2 items-center justify-between">
                  {/* Left side controls */}
                  <div className="flex items-center gap-1">
                    {/* Mode Selector - Hidden when listening on mobile */}
                    <div
                      className={`relative transition-opacity duration-200 ${isMobile && isListening
                        ? "opacity-0 w-0 h-0 overflow-hidden"
                        : "opacity-100"
                        }`}
                    >
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (isMobile) {
                              openMobileDrawer();
                            } else {
                              setShowPlusDropdown(!showPlusDropdown);
                            }
                          }}
                          className={`p-0 h-[36px] w-[36px] rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm bg-transparent ${isDarkMode
                            ? "text-white hover:bg-[#303030]"
                            : "text-gray-900 hover:bg-gray-200"
                            }`}
                          aria-label="More options"
                        >
                          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                        </Button>

                        {!isMobile && showPlusDropdown && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setShowPlusDropdown(false)}
                            />
                            <Card
                              className={`absolute bottom-0 left-0 mb-2 w-[220px] z-20 rounded-2xl shadow-lg border transition-all duration-150 max-h-[70vh] overflow-y-auto max-w-[calc(100vw-16px)] ${isDarkMode
                                ? "bg-[#1c1c1c] border-[#2e2e2e] backdrop-blur-xl"
                                : "bg-white border-gray-200 backdrop-blur-xl"
                                }`}
                            >
                              <div className="p-2 pt-1 rounded-2xl space-y-[4px]">

                                <div className="hidden sm:flex lg:hidden flex-col space-y-[4px]">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      photosInputRef.current?.click();
                                      setShowPlusDropdown(false);
                                    }}
                                    className={`w-full flex items-center justify-between p-2 mt-1 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                      ? "hover:bg-[#303030] text-white"
                                      : "hover:bg-gray-100 text-gray-900"
                                      }`}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <Image className={`w-[18px] h-[18px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                      <span className="text-sm">Add image</span>
                                    </div>
                                  </button>

                                  {hasLibraryImages && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onOpenLibrary?.();
                                        setShowPlusDropdown(false);
                                      }}
                                      className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                        ? "hover:bg-[#303030] text-white"
                                        : "hover:bg-gray-100 text-gray-900"
                                        }`}
                                    >
                                      <div className="flex items-center space-x-2">
                                        <Images className={`w-[18px] h-[18px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                        <span className="text-sm">Add Chat Images</span>
                                      </div>
                                    </button>
                                  )}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    attachInputRef.current?.click();
                                    setShowPlusDropdown(false);
                                  }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? "hover:bg-[#303030] text-white"
                                    : "hover:bg-gray-100 text-gray-900"
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Paperclip className={`w-[18px] h-[18px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                    <span className="text-sm">Add files</span>
                                  </div>
                                </button>

                                <button
                                  onClick={() => { openCanvas(null, null); setShowPlusDropdown(false); }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? "hover:bg-[#303030] text-white"
                                    : "hover:bg-gray-100 text-gray-900"
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Paintbrush className={`w-[18px] h-[18px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                    <span className="text-sm">Draw</span>
                                  </div>
                                </button>

                                <hr></hr>

                                <button
                                  onClick={() => { onModeChange('image'); onAgentChange(null); setShowPlusDropdown(false); }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? `text-white hover:bg-[#303030] ${isImageMode ? 'bg-[#252525]' : ''}`
                                    : `text-gray-900 hover:bg-gray-100 ${isImageMode ? 'bg-[#f3f4ff]' : ''}`
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <CreateImageIcon className={`w-6 h-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} size={26} />
                                    <span className="text-sm">Create image</span>
                                  </div>
                                  {isImageMode && (
                                    <Check className={`w-4 h-4 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />
                                  )}
                                </button>

                                <button
                                  onClick={() => { onModeChange('quiz'); onAgentChange(null); setShowPlusDropdown(false); }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? `text-white hover:bg-[#303030] ${isQuizMode ? 'bg-[#252525]' : ''}`
                                    : `text-gray-900 hover:bg-gray-100 ${isQuizMode ? 'bg-[#f3f4ff]' : ''}`
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <HelpCircle className={`w-[18px] h-[18px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                    <span className="text-sm">Quiz</span>
                                  </div>
                                  {isQuizMode && (
                                    <Check className={`w-4 h-4 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />
                                  )}
                                </button>

                                <button
                                  onClick={() => { onModeChange('web'); onAgentChange(null); setShowPlusDropdown(false); }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? `text-white hover:bg-[#303030] ${isWebSearchMode ? 'bg-[#252525]' : ''}`
                                    : `text-gray-900 hover:bg-gray-100 ${isWebSearchMode ? 'bg-[#f3f4ff]' : ''}`
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Globe className={`w-[18px] h-[18px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                    <span className="text-sm">Web search</span>
                                  </div>
                                  {isWebSearchMode && (
                                    <Check className={`w-4 h-4 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />
                                  )}
                                </button>

                                <button
                                  onClick={() => { onModeChange('deep-research'); onAgentChange(null); setShowPlusDropdown(false); }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? `text-white hover:bg-[#303030] ${isDeepResearchMode ? 'bg-[#252525]' : ''}`
                                    : `text-gray-900 hover:bg-gray-100 ${isDeepResearchMode ? 'bg-[#f3f4ff]' : ''}`
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Atom className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                                    <span className="text-sm">Deep research</span>
                                  </div>
                                  {isDeepResearchMode && (
                                    <Check className={`w-4 h-4 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />
                                  )}
                                </button>

                                <button
                                  onClick={() => {
                                    setShowAgentModal(true);
                                    setShowPlusDropdown(false);
                                  }}
                                  className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-200 ease-out text-left ${isDarkMode
                                    ? "hover:bg-[#303030] text-white"
                                    : "hover:bg-gray-100 text-gray-900"
                                    }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <img
                                      src="/ai-svg-oreog.svg"
                                      alt="AI"
                                      className={`w-4 h-4 ${isDarkMode ? "opacity-80" : "opacity-90"}`}
                                    />
                                    <span className="text-sm">Agent</span>
                                  </div>
                                </button>
                              </div>
                            </Card>
                          </>
                        )}
                      </div>
                    </div>

                    {showAgentsQuickButton && !(isMobile && isListening) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onPointerDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => {
                          setShowAgentDropdown(true);
                          setShowPlusDropdown(false);
                          setShowMobileDrawer(false);
                        }}
                        className={`h-[36px] rounded-full px-3 flex items-center gap-2 transition-colors duration-150 group backdrop-blur-sm bg-transparent ${isDarkMode
                          ? "text-white hover:bg-[#303030]"
                          : "text-gray-900 hover:bg-gray-200"
                          } ${selectedAgent ? 'max-[420px]:hidden' : ''}`}
                        aria-label="Open agents"
                        type="button"
                      >
                        <img
                          src="/ai-svg-oreog.svg"
                          alt="AI"
                          className={`w-4 h-4 ${isDarkMode ? "opacity-80" : "opacity-90"}`}
                        />
                        {<span className="text-sm">Agents</span>}
                      </Button>
                    )}

                    {isMobile && (
                      <Drawer
                        open={showMobileDrawer}
                        onOpenChange={(open) => {
                          if (!open) {
                            closeMobileDrawer();
                          }
                        }}
                      >
                        <DrawerContent
                          showHandle={false}
                          className={`mx-auto flex w-full max-w-lg flex-col rounded-t-3xl border ${isDarkMode ? 'border-[#333] bg-[#1F1F1F]/95' : 'border-border/40 bg-background/95'} pb-3 shadow-2xl backdrop-blur-lg h-[65vh] sm:h-[60vh]`}
                        >
                          <div className="flex h-full flex-col">
                            <div className="flex-1 overflow-y-auto overscroll-contain sm:px-5 px-4 pb-4 pt-2">
                              <div className="grid grid-cols-4 gap-2 py-2">
                                <button
                                  type="button"
                                  onClick={() => { cameraInputRef.current?.click(); closeMobileDrawer(); }}
                                  className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                                >
                                  <span className="flex items-center justify-center">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 7L10.5 5H13.5L15 7H19C20.1046 7 21 7.89543 21 9V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V9C3 7.89543 3.89543 7 5 7H9Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 17C13.6569 17 15 15.6569 15 14C15 12.3431 13.6569 11 12 11C10.3431 11 9 12.3431 9 14C9 15.6569 10.3431 17 12 17Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                  </span>
                                  <span className="text-sm select-none">Camera</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { photosInputRef.current?.click(); closeMobileDrawer(); }}
                                  className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                                >
                                  <span className="flex items-center justify-center">
                                    <CreateImageIcon className="w-[22px] h-[22px]" />
                                  </span>
                                  <span className="text-sm select-none">Photos</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { fileInputRef.current?.click(); closeMobileDrawer(); }}
                                  className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                                >
                                  <span className="flex items-center justify-center">
                                    <Paperclip className="w-[22px] h-[22px]" />
                                  </span>
                                  <span className="text-sm select-none">Files</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { onOpenLibrary?.(); closeMobileDrawer(); }}
                                  className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                                >
                                  <span className="flex items-center justify-center">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 20V6C6 4.89543 6.89543 4 8 4H18C19.1046 4 20 4.89543 20 6V20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M6 20H18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M9 8H17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M9 12H17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                  </span>
                                  <span className="text-sm select-none">Library</span>
                                </button>
                              </div>
                              <hr className="my-1"></hr>
                              <div className="flex flex-col gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => { onModeChange('image'); onAgentChange(null); closeMobileDrawer(); }}
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150 ${isDarkMode ? `hover:bg-white/5 active:bg-white/10 ${isImageMode ? 'bg-white/10' : ''}` : `hover:bg-gray-200 active:bg-gray-300 ${isImageMode ? 'bg-gray-200' : ''}`}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <CreateImageIcon className={`w-6 h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                                    <div className="flex flex-col items-start">
                                      <span className="text-base select-none">Create image</span>
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} select-none`}>Visualize anything</span>
                                    </div>
                                  </div>
                                  {isImageMode && <Check className={`w-5 h-5 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { onModeChange('deep-research'); onAgentChange(null); closeMobileDrawer(); }}
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150 ${isDarkMode ? `hover:bg-white/5 active:bg-white/10 ${isDeepResearchMode ? 'bg-white/10' : ''}` : `hover:bg-gray-200 active:bg-gray-300 ${isDeepResearchMode ? 'bg-gray-200' : ''}`}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Atom className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                                    <div className="flex flex-col items-start">
                                      <span className="text-base select-none">Deep research</span>
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} select-none`}>Get a detailed report</span>
                                    </div>
                                  </div>
                                  {isDeepResearchMode && <Check className={`w-5 h-5 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { onModeChange('web'); onAgentChange(null); closeMobileDrawer(); }}
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150 ${isDarkMode ? `hover:bg-white/5 active:bg-white/10 ${isWebSearchMode ? 'bg-white/10' : ''}` : `hover:bg-gray-200 active:bg-gray-300 ${isWebSearchMode ? 'bg-gray-200' : ''}`}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Globe className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                                    <div className="flex flex-col items-start">
                                      <span className="text-base">Web search</span>
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Find real-time news and info</span>
                                    </div>
                                  </div>
                                  {isWebSearchMode && <Check className={`w-5 h-5 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { setShowAgentModal(true); closeMobileDrawer(); }}
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150 ${isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300'}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <img src="/ai-svg-oreog.svg" alt="AI" className={`w-5 h-5 ${isDarkMode ? 'opacity-80' : 'opacity-90'}`} />
                                    <div className="flex flex-col items-start">
                                      <span className="text-base">Agents</span>
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Choose an agent</span>
                                    </div>
                                  </div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { openCanvas(null, null); closeMobileDrawer(); }}
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150 ${isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300'}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Paintbrush className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                                    <div className="flex flex-col items-start">
                                      <span className="text-base">Draw</span>
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sketch and annotate</span>
                                    </div>
                                  </div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => { onModeChange('quiz'); onAgentChange(null); closeMobileDrawer(); }}
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150 ${isDarkMode ? `hover:bg-white/5 active:bg-white/10 ${isQuizMode ? 'bg-white/10' : ''}` : `hover:bg-gray-200 active:bg-gray-300 ${isQuizMode ? 'bg-gray-200' : ''}`}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <HelpCircle className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                                    <div className="flex flex-col items-start">
                                      <span className="text-base select-none">Quiz</span>
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} select-none`}>Get an interactive quiz</span>
                                    </div>
                                  </div>
                                  {isQuizMode && <Check className={`w-5 h-5 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </DrawerContent>
                      </Drawer>
                    )}

                    {(selectedAgent || (!selectedAgent && currentMode !== 'chat')) && !(isMobile && isListening) && (
                      <button
                        onClick={() => {
                          if (selectedAgent) {
                            onAgentChange(null);
                            onModeChange('chat');
                          } else {
                            onModeChange('chat');
                          }
                        }}
                        className={`h-[36px] rounded-full pr-3 flex items-center gap-2 transition-colors duration-150 group backdrop-blur-sm ${isImageBackground
                          ? (isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white border border-white/10' : 'bg-white/40 hover:bg-white/60 text-black border border-black/10')
                          : (isDarkMode
                            ? "text-[#99CEFF] hover:bg-[#394A5B] bg-white/00 border border-blue-500/20"
                            : "text-[#0285FF] hover:bg-[#EBF4FF] bg-white/00 border border-blue-300/40")
                          } ${selectedAgent ? 'pl-2' : 'pl-3'}`}
                        aria-label={selectedAgent ? `Remove ${selectedAgent.name}` : `Disable ${getCurrentModeText}`}
                        type="button"
                      >
                        <span className="flex items-center gap-2">
                          <span className="group-hover:hidden">
                            {selectedAgent ? (
                              <img
                                src={availableAgents.find((a) => a.id === selectedAgent.id)?.icon || selectedAgent.icon}
                                alt={selectedAgent.name}
                                className="w-7 h-7 rounded-md bg-transparent object-contain"
                              />
                            ) : currentMode === 'image' ? (
                              <CreateImageIcon className="w-5 h-5" />
                            ) : currentMode === 'web' ? (
                              <Globe className="w-5 h-5" />
                            ) : (
                              <Atom className="w-5 h-5" />
                            )}
                          </span>
                          <span className="hidden group-hover:block p-0.5">
                            <X className={`bg-sky-500/20 p-1 rounded-full ${selectedAgent ? 'w-6 h-6' : 'w-5 h-5'}`} />
                          </span>
                          <span className="text-sm select-none truncate">
                            {selectedAgent ? selectedAgent.name : getCurrentModeText}
                          </span>
                        </span>
                      </button>
                    )}

                    {isImageMode && !selectedAgent && !(isMobile && isListening) && (
                      <div className="relative" ref={aspectDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsAspectDropdownOpen((open) => !open)}
                          className={
                            `h-[36px] rounded-full px-3 flex items-center gap-2 transition-colors duration-150 backdrop-blur-sm ` +
                            (isDarkMode
                              ? 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                              : 'bg-gray-100 hover:bg-gray-200 text-black border border-gray-200')
                          }
                          aria-label="Select aspect ratio"
                        >
                          <span className="flex items-center justify-center">
                            <AspectRatioIcon ratio={selectedAspectRatio} />
                          </span>
                          <span className="text-sm select-none">{selectedAspectRatio}</span>
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={isDarkMode ? 'text-white/80 hidden xs:flex' : 'text-gray-700 hidden xs:flex'}
                          >
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>

                        {isAspectDropdownOpen && (
                          <div
                            className={
                              `absolute bottom-full left-0 mb-2 min-w-[140px] rounded-xl overflow-hidden border shadow-lg z-50 backdrop-blur-sm ` +
                              (isDarkMode
                                ? 'bg-[#1E1E1E] border-white/10 text-white'
                                : 'bg-white border-gray-200 text-gray-900')
                            }
                          >
                            <div className="p-0">
                              {aspectRatios.map((r) => (
                                <button
                                  key={r.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedAspectRatio(r.id);
                                    setIsAspectDropdownOpen(false);
                                  }}
                                  className={
                                    `w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ` +
                                    (isDarkMode
                                      ? 'hover:bg-white/10'
                                      : 'hover:bg-gray-100')
                                  }
                                >
                                  <span className="flex items-center justify-center">
                                    <AspectRatioIcon ratio={r.id} />
                                  </span>
                                  <span>{r.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right side controls */}
                  <div
                    className={`flex items-center gap-1 transition-colors duration-150 ${isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    <SendControls
                      onSend={onSend}
                      onStop={onStop}
                      onOpenVoiceMode={() => {
                        setIsVoiceModeOpen(true);
                        setShowPlusDropdown(false);
                        setShowAgentDropdown(false);
                        setIsAspectDropdownOpen(false);
                      }}
                      inputValue={inputValue}
                      isLoading={isLoading}
                      canSendMessage={canSendMessage}
                      isDarkMode={isDarkMode}
                      onSpeechTranscript={(transcript) => {
                        // Append the transcript to existing input instead of replacing it
                        const newValue = inputValue ? `${inputValue} ${transcript}` : transcript;
                        onInputChange(newValue);
                        // Auto-focus the textarea after speech input
                        setTimeout(() => {
                          textareaRef.current?.focus();
                        }, 0);
                      }}
                      onListeningChange={(listening) => {
                        setIsListening(listening);
                        // Close any open dropdowns when starting to listen
                        if (listening) {
                          setShowPlusDropdown(false);
                          setShowAgentDropdown(false);
                          setIsAspectDropdownOpen(false);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <VoiceModeOverlay
          isOpen={isVoiceModeOpen}
          isDarkMode={isDarkMode}
          onClose={() => setIsVoiceModeOpen(false)}
          onOpenPlus={() => {
            setIsVoiceModeOpen(false);
            setShowPlusDropdown(true);
          }}
        />
        <AIAgentSelector
          selectedAgent={selectedAgent}
          onAgentChange={handleAgentModalChange}
          isDarkMode={isDarkMode}
          isOpen={showAgentModal}
          onOpenChange={setShowAgentModal}
          hideTrigger={true}
        />
        {showCanvas && (
          <DrawCanvasModal
            isOpen={true}
            onClose={() => {
              setShowCanvas(false);
              setCanvasInitialImage(null);
            }}
            onDone={handleCanvasDone}
            isDarkMode={isDarkMode}
            initialImage={canvasInitialImage || undefined}
          />
        )}
      </div>
    );
  }
);

ChatInputArea.displayName = "ChatInputArea";

